import { ChangeEvent, FormEvent, useState } from "react";
import EmailFormInput from "./EmailFormInput";
import FormFooter from "./FormFooter";
import FormHeader from "./FormHeader";
import PasswordFormInput from "./PasswordFormInput";
import TextFormInput from "./TextFormInput";
import nProgress from "nprogress";
import { useRouter } from "next/router";
import validateEmail from "@/functions/ValidateEmail";
import validatePassword from "@/functions/ValidatePassword";
import Divider from "./Divider";
import SubmitButton from "./SubmitButton";
import useRegister from "@/hooks/useRegister";
import encryptData from "@/functions/encryptInput";
import { useCheckEmail } from "@/services/AuthenticationService";
import GoogleAuthButton from "./GoogleAuthButton";
import Modal from "../Modal";
import TermsConditions from "../TermsConditions/TermsConditions";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";

type RegisterFormInput = {
    fullName: string
    email: string;
    password: string;
    confirmPassword: string
}

interface RegisterValidation {
    fullName : string
    email : string
    password : string
    confirmPassword : string
}

export default function RegisterForm() {

    const [formData, setFormData] = useState<RegisterFormInput>({
        fullName: "",
        email: "",
        password: "",
        confirmPassword: ""
    });

    const [validation, setValidation] = useState<RegisterValidation>({
        fullName: "",
        email: "",
        password: "",
        confirmPassword: ""
    });

    const [isTCModalOpen, setIsTCModalOpen] = useState<boolean>(false);

    const setRegisterData = useRegister((state) => state.setRegisterData);

    const router = useRouter();

    const { checkEmail } = useCheckEmail();

    const isNameValid = (name: string) => {
        const isNameFilled = name != '';
        const message = isNameFilled ? "" : "Please enter your name";
        setValidation((v) => v = {...v, fullName: message});
        return isNameFilled;
    }

    const isEmailValid = async (email: string) => {

        const encryptedEmail = await encryptData(email);
        const { isValid, validationMessage } = validateEmail(email);
        if (!isValid) {
          setValidation((v) => ({ ...v, email: validationMessage }));
          return false;
        }
        try {
            const response = await checkEmail(encryptedEmail)
    
          
          if (response.data?.isExist) {
            setValidation((v) => ({
              ...v,
              email: "This email is already registered. Please use a different one.",
            }));
            return false;
          }
    
          setValidation((v) => ({ ...v, email: "" }));
          return true;
        } catch (error) {
          console.error("Error checking email:", error);
          setValidation((v) => ({
            ...v,
            email: "There was an error checking the email. Please try again later.",
          }));
          return false;
        }
      };

    const isPasswordValid = (password: string) => {
        const { isValid, validationMessage } = validatePassword(password, true, true); 
        const message = isValid ? "" : validationMessage;
        setValidation((v) => v = {...v, password: message});
        return isValid;
    }

    const isPasswordConfirmationValid = (password: string) => {
        const { isValid, validationMessage } = validatePassword(password, false, true); 
        const message = isValid ? "" : validationMessage + " confirmation";
        setValidation((v) => v = {...v, confirmPassword: message});
        return isValid;
    }

    const isFormValid = async () => {
        const { fullName, email, password, confirmPassword } = formData;
    
        const isNameInputValid = isNameValid(fullName);
        const isEmailInputValid = await isEmailValid(email); 
        const isPasswordInputValid = isPasswordValid(password);
        const isPasswordConfirmationInputValid = isPasswordConfirmationValid(confirmPassword);
    
        if (isPasswordConfirmationInputValid && password != confirmPassword)
            {
                setValidation((v) => v = {...v, 
                   confirmPassword: "The password and confirmation password do not match."
                });
                return;
            }
            
        return (
          isNameInputValid &&
          isEmailInputValid &&
          isPasswordInputValid &&
          isPasswordConfirmationInputValid
        );
      };

      const handleRegister = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
    
        const isValid = await isFormValid();
    
        if (!isValid) return;
    
        nProgress.start();
        register();
        nProgress.done();
    };
    

    const handleInput = (e: ChangeEvent<HTMLInputElement>) => {
        const { id, value } = e.currentTarget;
        setFormData({...formData, [id]: value});
    }

    const register = () => {
        setRegisterData(formData);
        router.push(`/profileDetail`);
    }
    
    return (
        <div className="flex justify-center items-center min-h-screen m-2">
            <div className="bg-white p-12 rounded-xl mx-auto my-20 lg:mx-20">
                <form onSubmit={handleRegister} noValidate>
                    <FormHeader headerText="Create Accelist Career Account" />

                    <TextFormInput
                        id="fullName"
                        value={formData.fullName}
                        onChange={handleInput}
                        textValidationMessage={validation.fullName}
                        placeholder="Name"
                    />

                    <EmailFormInput 
                        id="email"
                        value={formData.email}
                        onChange={handleInput}
                        validationMessage={validation.email}
                        autoFocus={false}
                    />

                    <PasswordFormInput 
                        id="password"
                        value={formData.password}
                        onChange={handleInput}
                        validationMessage={validation.password}
                    />
                    
                    <p className="my-4 text-slate-400 text-sm w-auto px-6">
                        *Password must include 8 characters (a combination of numbers, uppercase, lowercase letters, and special characters)
                    </p>

                    <PasswordFormInput 
                        id="confirmPassword"
                        value={formData.confirmPassword}
                        onChange={handleInput}
                        validationMessage={validation.confirmPassword}
                        placeholder="Confirm Password"
                    />
                    <div className="mt-6"> 
                        <SubmitButton buttonText="Create Account" />
                    </div>

                    <FormFooter
                        footerText="Already have an account?"
                        actionText="Login"
                        actionHref="/login"
                    />

                    <Divider />

                    <GoogleAuthButton text={"Sign up with Google"}/>

                    <p className="my-4 text-slate-400 text-sm w-auto px-6">
                        {`By registering and using our website, you agree to our `}
                        <button onClick={() =>  setIsTCModalOpen(true)} className="text-[#93885B] font-bold">
                            {`Terms and Conditions`}
                        </button>
                        
                    </p>

                </form>

                <Modal
                    open={isTCModalOpen}
                    modalTitle={
                        <div className="flex justify-between w-full text-3xl border-b-4 ">
                            <div className="flex-1"></div>
                            <div className="flex-3">
                                <h1 className="font-semibold text-center text-[#93885B]">
                                    Terms and Conditions
                                </h1>
                            </div>
                            <div className="flex-1 text-right font-extrabold mr-4">
                                <button onClick={() => setIsTCModalOpen(false)}><FontAwesomeIcon className="text-[#93885B]" icon={faTimes}></FontAwesomeIcon></button>
                            </div>
                        </div>}
                    closeButton={<></>}
                >
                    <div className="h-[70vh] max-w-[80vw] md:max-w-[30vw] overflow-y-auto overflow-x-hidden p-4 md:p-6">
                        <TermsConditions />
                    </div>
                </Modal>
            </div>
        </div>
    )
}